import { Injectable } from '@angular/core';
import { APIResponse } from 'src/app/core/models/api-response';
import { IAgent, IAgentFilterRequest } from '../models/agents.models';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ICatalog, ICatalogFilterRequest } from '../../catalogs/models/catalogs.models';
import { IPageCount } from 'src/app/shared/models/page.model';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class AgentService {
  constructor(private http: HttpClient, private translateService: TranslateService) {}

  public getAgents(
    filter: IAgentFilterRequest | undefined,
    search: string | undefined,
    subsidiary_id: number | undefined,
  ): Observable<APIResponse<IAgent>> {
    let params = new HttpParams();
    if (filter) {
      params = params.set('filters', JSON.stringify(filter));
    }
    if (search) {
      params = params.set('key', search);
    }
    if (subsidiary_id) {
      params = params.set('subsidiary_id', subsidiary_id);
    }
    return this.http.get<APIResponse<IAgent>>(
      environment.BASE_URL_API + 'agents',
      { params }
    );
  }

  public getAgentById(id: number, params?: { start_date?: string, end_date?: string }): Observable<APIResponse<IAgent>> {
    const httpParams = new HttpParams()
      .set('start_date', params?.start_date ?? '')
      .set('end_date', params?.end_date ?? '');

    return this.http.get<APIResponse<IAgent>>(
      environment.BASE_URL_API + 'agents/' + id,
      { params: httpParams }
    );
  }

  public addAgent(agent: Omit<IAgent, 'id'>): Observable<APIResponse<IAgent>> {
    return this.http.post<APIResponse<IAgent>>(
      environment.BASE_URL_API + 'agents',
      agent
    );
  }

  public updateAgent(
    id: number,
    agent: Partial<IAgent>
  ): Observable<APIResponse<IAgent>> {
    return this.http.patch<APIResponse<IAgent>>(
      environment.BASE_URL_API + 'agents/' + id,
      agent
    );
  }

  public deleteAgent(agent_ids: number[]): Observable<APIResponse<number>> {
    return this.http.post<APIResponse<number>>(
      environment.BASE_URL_API + 'agents/bulk/delete',
      { agent_ids }
    );
  }

  public getCatalogs(
    page: IPageCount,
    filter?: ICatalogFilterRequest | undefined,
    search?: string | undefined
  ): Observable<APIResponse<ICatalog>> {
    const params = new HttpParams()
      .set('page', page.number)
      .set('limit', page.size)
      .set('filters', JSON.stringify(filter))
      .set('key', search!);

    return this.http.get<APIResponse<ICatalog>>(
      environment.BASE_URL_API + 'agent_pricelists',
      { params }
    );
  }

  public exportAgents(agents: IAgent[], filenamePrefix = 'agents'): void {
    if (!agents.length) return;

    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');

    const dateStr = [
      now.getFullYear(),
      pad(now.getMonth() + 1),
      pad(now.getDate()),
    ].join('-');

    const timeStr = [
      pad(now.getHours()),
      pad(now.getMinutes()),
      pad(now.getSeconds()),
    ].join('-');

    const filename = `${filenamePrefix}_${dateStr}_${timeStr}.csv`;

    const headers = [
      this.translateService.instant('agent.listing.fullname') || 'Agent name',
      this.translateService.instant('agent.listing.tags') || 'Tags',
      this.translateService.instant('agent.listing.portfolio') || 'Portfolio',
      this.translateService.instant('agent.listing.orders') || 'Orders',
      this.translateService.instant('agent.listing.total_orders') || 'Total orders €',
      // this.translateService.instant('agent.listing.commissions') || 'Commissions',
      // this.translateService.instant('agent.listing.performance') || 'Performance',
      this.translateService.instant('agent.listing.last_order') || 'Last order',
    ];


    const csvRows = [headers.join(',')];

    for (const agent of agents) {
      const row = [
        `${agent.first_name} ${agent.last_name}`,
        `${agent.tags.map((tag) => tag.name).join(' | ')}`,
        agent.nbr_companies ?? '',
        agent.order_count ?? '',
        agent.total_amount ? agent.total_amount.toFixed(2) + ' €' : '',
        // '', // TODO: Implement commissions when its returned by the API
        // '', // TODO: Implement performance when its returned by the API
        agent.last_order_date ?? '',
      ];
      csvRows.push(row.join(','));
    }

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
